/**
 * Sheets 31 and 33, transmit a run (mission 7 WP10 T053, T054, T056; FR-011, FR-012, FR-022, FR-028,
 * NFR-007, NFR-008; TX.2, TX.3, TX.4, TX.5, TX.6, TX.8, TX.9, T.2, ANA.4, X.10, GEN.4, GEN.6, AV.7, NF.7,
 * UC-01; squad U3, U4, U8, U10, U19, P15, P22; OR-9, OR-10; contracts/ui-api.md "Sheets 31 and 33").
 *
 * The run is read first. When mission 6's `transmitGate` hides Transmit (FR-022: the gate is unchanged),
 * the page is the `h1` and one sentence, X.10's `pathBNotTransmissible`, GEN.4's `runNotReady` or GEN.6's
 * NOT DELIVERABLE badge with `transmitNotDeliverable`, and it reads nothing more and offers nothing to
 * submit (31/14). Otherwise it reads the bureaus, the processing terms and the run's transmissions, and
 * renders in order:
 * - the gate line: `gateEligible`, or `gateAckRequired` with the ERROR badge and `SeverityLegend` beside it
 *   (ANA.4; OR-10, U4);
 * - the terms: accepted with version, time and email, or the blocked band with Accept, which opens
 *   `ProcessingTermsDialog` and keeps the selection (TX.3; 31/2, 31/13);
 * - `T2Band`, verbatim (T.2; CLAUDE.md §3.5);
 * - any refusal as a band, above the table, the page staying usable;
 * - `BureauTable`; the selection is every verified bureau, or only `?bureau=` when it names one, applied
 *   once the bureaus read resolves however late (U19);
 * - the TX.8 info band: nothing is charged, and the credits are never read or refreshed here;
 * - the Transmit button, secondary in both states (C-015). At 0 ERROR it reads `transmitToN` and posts.
 *   At ERROR > 0 it carries `TransmitControl`'s label and reason line, written literally here because
 *   `TransmitControl` exports neither and is not edited (FR-022; a test holds the two equal, P22), and it
 *   opens `AckDialog` (sheet 33). With nothing selected it is disabled with `selectAtLeastOne`, and while
 *   the terms are not accepted with `termsBlockedRemedy`, so the two dialogs never open together.
 * After the 202: `Progress`, read from the per-run query, which polls every 2 s while an attempt is
 * pending and stops when none is or on an error, keeping its data (NFR-008, OR-9). A resolved
 * `auth_failed` re-reads the bureaus (research §1.24).
 *
 * Mission 9 WP14 T089 (51/9; FR-015; B.1; U11; R23): while `useBillingGate` refuses (read-only, pending)
 * the Transmit button is disabled with the gate's reason, before the terms and selection reasons; it
 * stays secondary, and `T2Band` renders verbatim in every state (CLAUDE.md §3.5).
 *
 * Mission 10 WP18 T108 (FR-019 sheet 31, FR-004, FR-022, C-007; spec Assumptions "Retention-0 triggers"; D.2;
 * OR-15, U31): the page reads the retention window's first page (`retentionQuery()`; `GET /v1/tenant` is not
 * extended). At `retention_days = 0` it states `RetentionZeroWarning`, with no acknowledgment, and register 37
 * `retentionZeroSecondBureau` beneath it, as a D.2 band after T.2. When the shell already shows its billing
 * band (`billingBandShown`, the banner's own predicate: read-only or dunning), T.2 makes two, so the warning
 * renders inline beside the Transmit button instead (§7.5; U31). The warning gates nothing here, and an unread
 * window shows none (fail open stands on 31; cycle 2 ruling); the Transmit button is unchanged.
 */
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { useTenant } from '@/components/trellis/AuthGate'
import { Band } from '@/components/trellis/Band'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { ProcessingTermsDialog } from '@/components/trellis/ProcessingTermsDialog'
import { RetentionZeroWarning } from '@/components/trellis/RetentionZeroWarning'
import { SeverityBadge } from '@/components/trellis/SeverityBadge'
import { SeverityLegend } from '@/components/trellis/SeverityLegend'
import { T2Band } from '@/components/trellis/T2Band'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ApiError, InvalidIdError, type Problem } from '@/lib/api'
import { BUREAUS, type Bureau } from '@/lib/api/bureaus'
import type { Run, RunState } from '@/lib/api/runs'
import { useBillingGate } from '@/lib/billing-gate'
import type { ProcessingTermsStatus } from '@/lib/api/terms'
import { transmit, type TransmitAccepted, type TransmitIneligibleReason } from '@/lib/api/transmissions'
import { Mono, count, date, isoDateTime, shortId, time } from '@/lib/format'
import { transmitGate, type TransmitGate } from '@/lib/gates'
import { ERROR_ACK } from '@/lib/mandatory-copy'
import type { ProblemOverrides } from '@/lib/problems'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import {
  bureausQuery,
  creditsQuery,
  invalidateTransmissions,
  keys,
  processingTermsQuery,
  retentionQuery,
  runQuery,
  runTransmissionsQuery,
} from '@/lib/queries'

import { billingBandShown } from '../shell/BillingBanner'
import { AckDialog } from './transmit/AckDialog'
import { BureauTable } from './transmit/BureauTable'
import { Progress } from './transmit/Progress'

/** Refusals that change what the bureaus read says, so it is read again (contracts/ui-api.md). */
const BUREAU_REFUSALS = new Set(['bureau-not-verified', 'bureau-disabled', 'host-not-allowed'])
const TERMS_REFUSAL = 'processing-terms-required'
const ACK_REFUSAL = 'error-acknowledgment-required'

function slugOf(problem: Problem): string {
  return problem.type.slice(problem.type.lastIndexOf('/') + 1)
}

function isBureau(value: string | null): value is Bureau {
  return value !== null && (BUREAUS as readonly string[]).includes(value)
}

/** `text` with the first occurrence of `part` in mono. */
function withMono(text: string, part: string) {
  const at = text.indexOf(part)
  if (at < 0) return <>{text}</>
  return (
    <>
      {text.slice(0, at)}
      <Mono>{part}</Mono>
      {text.slice(at + part.length)}
    </>
  )
}

/** Register 17's run label with the short id in mono. */
function RunLabel({ runId }: { runId: string }) {
  return withMono(PROVISIONAL_COPY.runLabel.render({ runId }), shortId(runId))
}

function Loading() {
  return (
    <div className="flex flex-col gap-6">
      <Skeleton className="h-6 w-80" />
      <Skeleton className="h-16 w-full rounded-lg" />
      <Skeleton className="h-48 w-full rounded-lg" />
    </div>
  )
}

function ReadFailure({ error }: { error: unknown }) {
  const notFound = error instanceof InvalidIdError || (error instanceof ApiError && error.problem.status === 404)
  if (notFound) return <Band kind="info">{copyText('notFound')}</Band>
  return (
    <Band kind="blocked">
      <p>{copyText('genericError')}</p>
    </Band>
  )
}

/** The first run-level block, Path B first, as `run.TransmitBlocked()` orders it. */
function blockOf(run: Run): TransmitIneligibleReason {
  if (run.path !== 'generate') return 'X.10'
  if (run.state !== 'ready') return 'GEN.4'
  return 'GEN.6'
}

/** 31/14: the one sentence for a run Transmit does not apply to. Nothing here can be submitted. */
function Ineligible({ reason, state }: { reason: TransmitIneligibleReason; state: RunState | undefined }) {
  if (reason === 'X.10') {
    return (
      <Band kind="info" requirement="X.10">
        <p>{copyText('pathBNotTransmissible')}</p>
      </Band>
    )
  }
  if (reason === 'GEN.4') {
    return (
      <Band kind="info" requirement="GEN.4">
        <p>{PROVISIONAL_COPY.runNotReady.render(state === undefined ? {} : { state })}</p>
      </Band>
    )
  }
  return (
    <Band kind="info" requirement="GEN.6">
      <div className="flex flex-col items-start gap-2">
        {/* mission 7 WP14: J3, the badge text is a register entry (P22). */}
        <Badge status="not-deliverable">{copyText('notDeliverableBadge')}</Badge>
        <p>{copyText('transmitNotDeliverable')}</p>
      </div>
    </Band>
  )
}

function GateLine({ gate }: { gate: Exclude<TransmitGate, { kind: 'absent' }> }) {
  if (gate.kind === 'ready') {
    return (
      <div data-gate-line="">
        <p className="text-body text-navy-950">{copyText('gateEligible')}</p>
      </div>
    )
  }
  return (
    <div data-gate-line="" className="flex flex-col gap-3">
      <p className="flex items-center gap-2 text-body text-navy-950">
        <SeverityBadge severity="ERROR" />
        <span>{withMono(PROVISIONAL_COPY.gateAckRequired.render({ count: gate.errors }), count(gate.errors))}</span>
      </p>
      {/* ANA.4: the legend renders wherever a severity badge does (OR-10). */}
      <SeverityLegend />
    </div>
  )
}

function TermsStatus({ terms, email, blocked, onAccept }: { terms: ProcessingTermsStatus; email: string; blocked: boolean; onAccept(): void }) {
  if (blocked || !terms.accepted || !terms.accepted_at) {
    return (
      <Band
        kind="blocked"
        requirement="TX.3"
        action={
          <Button variant="secondary" size="sm" onClick={onAccept}>
            {copyText('termsAccept')}
          </Button>
        }
      >
        <p>{copyText('termsBlockedRemedy')}</p>
      </Band>
    )
  }
  const at = `${date(terms.accepted_at)} ${time(terms.accepted_at)}`
  const text = PROVISIONAL_COPY.termsAccepted.render({ version: terms.accepted_version ?? terms.current_version, time: at, email })
  const i = text.indexOf(at)
  return (
    <p data-terms-status="" className="text-body text-navy-950">
      {text.slice(0, i)}
      <time dateTime={isoDateTime(terms.accepted_at)}>{at}</time>
      {text.slice(i + at.length)}
    </p>
  )
}

/** Sheet 31 at retention 0 (T108): the warning, no acknowledgment, and the second-bureau sentence. */
function RetentionZero({ hours, minutes, inline }: { hours: number; minutes: number; inline: boolean }) {
  const warning = (
    <RetentionZeroWarning hours={hours} minutes={minutes} inline={inline}>
      <p className="mt-1">{copyText('retentionZeroSecondBureau')}</p>
    </RetentionZeroWarning>
  )
  return inline ? (
    <Card padding="compact" className="mb-2">
      {warning}
    </Card>
  ) : (
    warning
  )
}

function TransmitBody({ run, gate }: { run: Run; gate: Exclude<TransmitGate, { kind: 'absent' }> }) {
  const runId = run.run_id
  const qc = useQueryClient()
  const [params] = useSearchParams()
  const tenant = useTenant()
  const bureaus = useQuery(bureausQuery())
  const terms = useQuery(processingTermsQuery())
  const log = useQuery(runTransmissionsQuery(runId))
  const [picked, setPicked] = useState<ReadonlySet<Bureau> | null>(null)
  const [termsOpen, setTermsOpen] = useState(false)
  const [ack, setAck] = useState<{ count: number; version: string } | null>(null)
  const [problem, setProblem] = useState<Problem | null>(null)
  const [failed, setFailed] = useState(false)
  const [accepted, setAccepted] = useState<{ body: TransmitAccepted; at: number } | null>(null)
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)
  const authFailedSeen = useRef(false)
  const button = useRef<HTMLButtonElement>(null)
  const reasonId = useId()
  const billing = useBillingGate()
  // T108: the window from the retention read's first page; the band from the credits read the gate observes.
  const retention = useInfiniteQuery(retentionQuery()).data?.pages[0]
  const billingAccess = useQuery(creditsQuery()).data?.billing
  const zero =
    retention?.retention_days === 0
      ? { hours: retention.terms.zero_fallback_hours, minutes: retention.terms.sweep_interval_minutes, inline: billingBandShown(billingAccess) }
      : null

  // Research §1.24: an attempt of this page's transmit that resolved auth_failed changed the bureau's status.
  useEffect(() => {
    if (!accepted || authFailedSeen.current || !log.data) return
    const ids = new Set(accepted.body.attempts.map((a) => a.transmission_id))
    if (log.data.entries.some((e) => ids.has(e.transmission_id) && e.result === 'auth_failed')) {
      authFailedSeen.current = true
      void qc.invalidateQueries({ queryKey: keys.bureaus })
    }
  }, [accepted, log.data, qc])

  if (bureaus.data === undefined || terms.data === undefined || log.data === undefined) {
    const error = bureaus.error ?? terms.error ?? log.error
    return error ? <ReadFailure error={error} /> : <Loading />
  }
  if (!log.data.transmit_eligible) {
    return <Ineligible reason={log.data.transmit_ineligible_reason ?? 'GEN.4'} state={log.data.run_state} />
  }

  const verified = new Set(bureaus.data.bureaus.filter((b) => b.status === 'verified').map((b) => b.bureau))
  // U19: derived once the bureaus read has resolved, however late; the reporter's own ticks win after that.
  const requested = params.get('bureau')
  const initial: ReadonlySet<Bureau> = isBureau(requested) ? new Set(verified.has(requested) ? [requested] : []) : verified
  const current = picked ?? initial
  const chosen = BUREAUS.filter((b) => current.has(b) && verified.has(b))
  const slug = problem ? slugOf(problem) : null
  const termsBlocked = slug === TERMS_REFUSAL || !terms.data.accepted
  const hostRefused = slug === 'host-not-allowed' && problem && isBureau(problem.bureau ?? null) ? (problem.bureau as Bureau) : null
  const reason =
    billing.reason ?? (termsBlocked ? copyText('termsBlockedRemedy') : chosen.length === 0 ? copyText('selectAtLeastOne') : null)
  const acknowledge = gate.kind === 'acknowledge'
  const overrides: ProblemOverrides = {
    // The run's own log rather than the tenant log (contracts/ui-api.md "Problem overrides").
    'transmission-in-progress': {
      band: 'info',
      remedy: 'transmissionInProgress',
      to: `/app/runs/${encodeURIComponent(runId)}/transmissions`,
    },
  }

  function toggle(bureau: Bureau, checked: boolean) {
    setPicked((prev) => {
      const next = new Set(prev ?? current)
      if (checked) next.add(bureau)
      else next.delete(bureau)
      return next
    })
  }

  function onAccepted(body: TransmitAccepted) {
    authFailedSeen.current = false
    setAck(null)
    setProblem(null)
    setFailed(false)
    setAccepted({ body, at: Date.now() })
    void invalidateTransmissions(qc, runId)
  }

  function onProblem(p: Problem) {
    setAck(null)
    const s = slugOf(p)
    if (s === ACK_REFUSAL) {
      // TX.4 at a count the page did not know: the modal opens with the served count and wording.
      setAck({
        count: typeof p.error_count === 'number' ? p.error_count : acknowledge ? gate.errors : 0,
        version: typeof p.ack_wording_version === 'string' ? p.ack_wording_version : ERROR_ACK.version,
      })
      return
    }
    setProblem(p)
    if (BUREAU_REFUSALS.has(s)) void qc.invalidateQueries({ queryKey: keys.bureaus })
  }

  async function submit() {
    if (inFlight.current || reason) return
    if (gate.kind === 'acknowledge') {
      setAck({ count: gate.errors, version: ERROR_ACK.version })
      return
    }
    inFlight.current = true
    setBusy(true)
    setProblem(null)
    setFailed(false)
    try {
      // 0 ERROR: the acknowledgment members are absent (contracts/transmission-api.md).
      onAccepted(await transmit(runId, { bureaus: chosen }))
    } catch (err) {
      if (err instanceof ApiError) onProblem(err.problem)
      else setFailed(true)
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  return (
    <>
      <GateLine gate={gate} />
      <TermsStatus
        terms={terms.data}
        email={tenant.data?.contact_email ?? ''}
        blocked={termsBlocked}
        onAccept={() => setTermsOpen(true)}
      />
      <T2Band />
      {zero !== null && !zero.inline ? <RetentionZero {...zero} /> : null}
      {problem && slug !== TERMS_REFUSAL ? <ProblemBand problem={problem} override={overrides} /> : null}
      {failed ? (
        <Band kind="blocked">
          <p>{copyText('genericError')}</p>
        </Band>
      ) : null}
      <div className="flex flex-col gap-2">
        <BureauTable
          runId={runId}
          bureaus={bureaus.data.bureaus}
          entries={log.data.entries}
          remoteFilename={log.data.remote_filename}
          selected={current}
          onToggle={toggle}
          hostRefused={hostRefused}
        />
        <p className="text-caption text-navy-600">{copyText('timesInUtc')}</p>
      </div>
      <Band kind="info" requirement="TX.8">
        <p>{copyText('tx8Band')}</p>
      </Band>
      <div className="flex flex-col items-start gap-1">
        {zero !== null && zero.inline ? <RetentionZero {...zero} /> : null}
        <Button
          ref={button}
          variant="secondary"
          loading={busy}
          onClick={() => void submit()}
          {...(acknowledge ? { 'aria-describedby': reasonId } : {})}
          {...(reason ? { disabled: true as const, disabledReason: reason } : {})}
        >
          {/* mission 7 WP14: J3, TransmitControl's label from the register (P22). */}
          {acknowledge ? copyText('transmitAcknowledge') : PROVISIONAL_COPY.transmitToN.render({ n: chosen.length })}
        </Button>
        {gate.kind === 'acknowledge' ? (
          // TransmitControl's reason line, the singular at 1 (§7.1; P22). mission 7 WP14: J3, from the register.
          <p id={reasonId} className="text-body-sm text-navy-600">
            {withMono(PROVISIONAL_COPY.transmitAckReason.render({ count: gate.errors }), count(gate.errors))}
          </p>
        ) : null}
      </div>
      {accepted ? (
        <Progress
          key={accepted.at}
          runId={runId}
          attempts={accepted.body.attempts}
          acceptedAt={accepted.at}
          entries={log.data.entries}
          interrupted={log.isError}
        />
      ) : null}
      <ProcessingTermsDialog
        open={termsOpen}
        onAccepted={() => {
          setTermsOpen(false)
          if (slug === TERMS_REFUSAL) setProblem(null)
        }}
        onCancel={() => setTermsOpen(false)}
      />
      <AckDialog
        open={ack !== null}
        runId={runId}
        count={ack?.count ?? 0}
        {...(ack && ack.version !== ERROR_ACK.version ? { servedVersion: ack.version } : {})}
        bureaus={chosen}
        email={tenant.data?.contact_email ?? ''}
        returnFocus={button}
        onCancel={() => setAck(null)}
        onAccepted={onAccepted}
        onProblem={onProblem}
      />
    </>
  )
}

export default function Transmit() {
  const { runId = '' } = useParams()
  const query = useQuery(runQuery(runId))

  if (query.data === undefined) {
    return <div className="flex max-w-6xl flex-col gap-6">{query.isError ? <ReadFailure error={query.error} /> : <Loading />}</div>
  }
  const run = query.data
  const gate = transmitGate(run)
  return (
    <div className="flex max-w-6xl flex-col gap-6">
      <h1 className="text-h1 text-navy-950">
        {copyText('crumb.transmit')} · <RunLabel runId={run.run_id} />
      </h1>
      {gate.kind === 'absent' ? <Ineligible reason={blockOf(run)} state={run.state} /> : <TransmitBody run={run} gate={gate} />}
    </div>
  )
}
